import {
  CameraOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  ScanOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import AppLayout from "../components/layout/AppLayout";
import BreadcrumbNav from "../components/layout/BreadcrumbNav";
import type { BarcodeReading, DataManCamera, DataManCameraStats } from "../types";
import { apiClient } from "../utils/api";
import { useWebSocket } from "../utils/useWebSocket";

const { Text } = Typography;

const DataManCamerasPage: React.FC = () => {
  const queryClient = useQueryClient();

  const [modalVisible, setModalVisible] = useState(false);
  const [editingCamera, setEditingCamera] = useState<DataManCamera | null>(null);
  const [form] = Form.useForm();

  // Detail drawer
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<DataManCamera | null>(null);
  const [latestReading, setLatestReading] = useState<BarcodeReading | null>(null);
  const [readingsPage, setReadingsPage] = useState(1);
  // Local real-time readings injected via WebSocket
  const [wsReadings, setWsReadings] = useState<BarcodeReading[]>([]);

  // --- Queries ---

  const {
    data: cameras = [],
    isLoading: loading,
    refetch: refetchCameras,
  } = useQuery({
    queryKey: ["dataManCameras"],
    queryFn: () => apiClient.getDataManCameras(),
  });

  const selectedCameraId = selectedCamera?.id;

  const { data: cameraStats = null } = useQuery<DataManCameraStats | null>({
    queryKey: ["cameraStats", selectedCameraId],
    queryFn: () => (selectedCameraId ? apiClient.getCameraStatistics(selectedCameraId) : null),
    enabled: !!selectedCameraId && drawerVisible,
  });

  const { data: readingsData, isLoading: readingsLoading } = useQuery({
    queryKey: ["cameraReadings", selectedCameraId, readingsPage],
    queryFn: () =>
      selectedCameraId
        ? apiClient.getCameraReadings(selectedCameraId, readingsPage, 20)
        : null,
    enabled: !!selectedCameraId && drawerVisible,
  });

  const readings = readingsPage === 1 && wsReadings.length > 0
    ? [...wsReadings, ...(readingsData?.data ?? [])].slice(0, 50)
    : readingsData?.data ?? [];
  const readingsTotal = readingsData?.meta?.total ?? 0;

  const { data: fetchedLatestReading = null } = useQuery<BarcodeReading | null>({
    queryKey: ["latestReading", selectedCameraId],
    queryFn: () => (selectedCameraId ? apiClient.getLatestSingleReading(selectedCameraId) : null),
    enabled: !!selectedCameraId && drawerVisible,
  });

  // Use WS latest reading if available, otherwise fetched
  const displayLatestReading = latestReading ?? fetchedLatestReading;

  // --- Mutations ---

  const createMutation = useMutation({
    mutationFn: (values: any) => apiClient.createDataManCamera(values),
    onSuccess: () => {
      message.success("Camera created");
      setModalVisible(false);
      queryClient.invalidateQueries({ queryKey: ["dataManCameras"] });
    },
    onError: () => message.error("Failed to save camera"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: any }) =>
      apiClient.updateDataManCamera(id, values),
    onSuccess: () => {
      message.success("Camera updated");
      setModalVisible(false);
      queryClient.invalidateQueries({ queryKey: ["dataManCameras"] });
    },
    onError: () => message.error("Failed to save camera"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteDataManCamera(id),
    onSuccess: () => {
      message.success("Camera deleted");
      queryClient.invalidateQueries({ queryKey: ["dataManCameras"] });
    },
    onError: () => message.error("Failed to delete camera"),
  });

  const triggerMutation = useMutation({
    mutationFn: (cameraId: string) => apiClient.triggerCamera(cameraId),
    onSuccess: () => message.success("Camera triggered"),
    onError: () => message.error("Failed to trigger camera"),
  });

  // --- WebSocket ---

  const { connected, connectionStatus, subscribe } = useWebSocket(undefined, {
    deviceType: "dataman",
    onMessage: (msg) => {
      if (msg.type === "barcode_scan" && msg.data) {
        if (selectedCamera && msg.device_id === selectedCamera.device_id) {
          setLatestReading(msg.data);
          setWsReadings((prev) => [msg.data, ...prev].slice(0, 50));
        }
        // Update camera status in query cache
        queryClient.setQueryData<DataManCamera[]>(["dataManCameras"], (old) =>
          old?.map((c) =>
            c.device_id === msg.device_id ? { ...c, status: "online" as const } : c
          )
        );
      }
    },
    onConnect: () => {
      message.success("DataMan real-time updates connected");
    },
    onDisconnect: () => {
      console.log("DataMan real-time updates disconnected");
    },
  });

  useEffect(() => {
    if (connected && cameras.length > 0) {
      const deviceIds = cameras.map((c) => c.device_id);
      subscribe(deviceIds);
    }
  }, [connected, cameras, subscribe]);

  // --- Handlers ---

  const openDetail = (camera: DataManCamera) => {
    setSelectedCamera(camera);
    setDrawerVisible(true);
    setReadingsPage(1);
    setLatestReading(null);
    setWsReadings([]);
  };

  const handleAdd = () => {
    setEditingCamera(null);
    form.resetFields();
    form.setFieldsValue({ port: 23 });
    setModalVisible(true);
  };

  const handleEdit = (camera: DataManCamera) => {
    setEditingCamera(camera);
    form.setFieldsValue(camera);
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingCamera) {
        updateMutation.mutate({
          id: editingCamera.id,
          values: { name: values.name, host: values.host, port: values.port },
        });
      } else {
        createMutation.mutate(values);
      }
    } catch {
      // validation error
    }
  };

  const gradeColor = (grade?: string) => {
    if (!grade) return "default";
    switch (grade) {
      case "A": return "green";
      case "B": return "blue";
      case "C": return "gold";
      case "D": return "orange";
      case "F": return "red";
      default: return "default";
    }
  };

  const columns: ColumnsType<DataManCamera> = [
    { title: "Device ID", dataIndex: "device_id", key: "device_id" },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "online" ? "green" : status === "error" ? "red" : "default"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Host",
      key: "host",
      render: (_, record) => `${record.host}:${record.port}`,
    },
    {
      title: "Last Seen",
      dataIndex: "last_seen",
      key: "last_seen",
      render: (v: string) => (v ? new Date(v).toLocaleString() : "-"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Tooltip title="View details">
            <Button icon={<EyeOutlined />} size="small" onClick={() => openDetail(record)} />
          </Tooltip>
          <Tooltip title="Trigger scan">
            <Button icon={<ScanOutlined />} size="small" onClick={() => triggerMutation.mutate(record.id)} />
          </Tooltip>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete this camera?" onConfirm={() => deleteMutation.mutate(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const readingColumns: ColumnsType<BarcodeReading> = [
    { title: "Barcode", dataIndex: "barcode_data", key: "barcode_data", ellipsis: true },
    {
      title: "Type",
      dataIndex: "barcode_type",
      key: "barcode_type",
      width: 100,
      render: (v: string) => <Tag>{v || "N/A"}</Tag>,
    },
    {
      title: "Grade",
      dataIndex: "overall_grade",
      key: "overall_grade",
      width: 70,
      render: (v: string) => <Tag color={gradeColor(v)}>{v || "N/A"}</Tag>,
    },
    {
      title: "Decode Time",
      dataIndex: "decode_time",
      key: "decode_time",
      width: 100,
    },
    {
      title: "Read Time",
      dataIndex: "read_time",
      key: "read_time",
      width: 180,
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ];

  return (
    <AppLayout
      title="DataMan Cameras"
      extraActions={
        <Space>
          <Tooltip title={`Real-time: ${connectionStatus}`}>
            <Badge
              status={
                connectionStatus === "connected"
                  ? "processing"
                  : connectionStatus === "connecting"
                  ? "warning"
                  : "error"
              }
              text={connected ? "Live" : connectionStatus === "connecting" ? "Connecting..." : "Offline"}
            />
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={() => refetchCameras()} loading={loading} />
        </Space>
      }
    >
      <div className="page-header-bar">
        <BreadcrumbNav />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Camera
        </Button>
      </div>

      <Card>
        <Table columns={columns} dataSource={cameras} rowKey="id" loading={loading} scroll={{ x: 800 }} />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingCamera ? "Edit Camera" : "Add Camera"}
        open={modalVisible}
        onOk={handleModalOk}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        onCancel={() => setModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          {!editingCamera && (
            <Form.Item
              name="device_id"
              label="Device ID"
              rules={[{ required: true, message: "Required" }]}
            >
              <Input placeholder="e.g. DM-001" />
            </Form.Item>
          )}
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Main Line Scanner" />
          </Form.Item>
          <Form.Item name="host" label="Host" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. 192.168.1.11" />
          </Form.Item>
          <Form.Item name="port" label="Port" rules={[{ required: true, message: "Required" }]}>
            <InputNumber min={1} max={65535} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Camera Detail Drawer */}
      <Drawer
        title={
          <Space>
            <CameraOutlined />
            {selectedCamera?.name || "Camera Details"}
          </Space>
        }
        width={typeof window !== 'undefined' && window.innerWidth < 768 ? '100%' : 720}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedCamera(null);
          setLatestReading(null);
          setWsReadings([]);
        }}
        extra={
          selectedCamera && (
            <Button
              icon={<ScanOutlined />}
              onClick={() => triggerMutation.mutate(selectedCamera.id)}
            >
              Trigger
            </Button>
          )
        }
      >
        {cameraStats && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Statistic title="Total Reads" value={cameraStats.total_readings} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic title="Today" value={cameraStats.today_readings} />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title="Status"
                value={cameraStats.status}
                valueStyle={{ color: cameraStats.status === "online" ? "#52c41a" : "#ff4d4f" }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title="Last Read"
                value={cameraStats.last_read_time ? new Date(cameraStats.last_read_time).toLocaleTimeString() : "-"}
              />
            </Col>
          </Row>
        )}

        {/* Latest Reading with Image */}
        {displayLatestReading && (
          <Card title="Latest Reading" size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Barcode">{displayLatestReading.barcode_data}</Descriptions.Item>
              <Descriptions.Item label="Type">{displayLatestReading.barcode_type || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Grade">
                <Tag color={gradeColor(displayLatestReading.overall_grade)}>
                  {displayLatestReading.overall_grade || "N/A"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Decode Time">{displayLatestReading.decode_time || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Symbology">{displayLatestReading.symbology_id || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Device">{displayLatestReading.device_name || "N/A"}</Descriptions.Item>
            </Descriptions>
            {displayLatestReading.image_data_uri && (
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Image
                  src={displayLatestReading.image_data_uri}
                  alt="Barcode capture"
                  style={{ maxWidth: "100%", maxHeight: 300 }}
                />
              </div>
            )}
          </Card>
        )}

        {/* Readings History */}
        <Card title="Reading History" size="small">
          <Table
            columns={readingColumns}
            dataSource={readings}
            rowKey="id"
            loading={readingsLoading}
            size="small"
            pagination={{
              current: readingsPage,
              total: readingsTotal,
              pageSize: 20,
              onChange: (page) => setReadingsPage(page),
              showTotal: (total) => <Text type="secondary">{total} readings</Text>,
            }}
          />
        </Card>
      </Drawer>
    </AppLayout>
  );
};

export default DataManCamerasPage;
